import { useMemo, useState } from "react";
import {
    Plus,
    Search,
    Lock as LockIcon,
    Trash2,
    Settings as SettingsIcon,
    Zap,
    KeyRound,
    Database,
    FileBadge,
    FileText,
    AlertTriangle,
} from "lucide-react";
import {
    C,
    CRED_TYPES,
    sBadge,
    sBtn,
    sCard,
    sFull,
    sPill,
    sTopBar,
    expColor,
    expLabel,
    fmtDate,
} from "../styles";

const ICONS = {
    zap: Zap,
    "key-round": KeyRound,
    database: Database,
    "file-badge": FileBadge,
    "file-text": FileText,
};

export default function Vault({
    entries,
    trashCount,
    onOpenEntry,
    onNewEntry,
    onLock,
    onNavigate,
}) {
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");

    const filtered = useMemo(() => {
        return entries.filter((e) => {
            const matchType = filter === "all" || e.type === filter;
            const hay = Object.values(e.fields || {})
                .join(" ")
                .toLowerCase();
            const matchSearch = hay.includes(search.toLowerCase());
            return matchType && matchSearch;
        });
    }, [entries, search, filter]);

    const expiring = useMemo(
        () =>
            entries.filter((e) => {
                const d = e.fields?.Expiry;
                if (!d) return false;
                const days = Math.ceil((new Date(d) - new Date()) / 86400000);
                return days !== null && days <= 30;
            }),
        [entries],
    );

    return (
        <div style={sFull} data-testid="vault-screen">
            {/* Top bar */}
            <div style={sTopBar}>
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <div
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.3em",
                            color: C.accent,
                        }}
                    >
                        HIKARI
                    </div>
                    <div
                        style={{
                            fontSize: 13,
                            color: C.text,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Vault
                    </div>
                    <div style={sBadge(C.dim)}>{entries.length}</div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    <IconBtn
                        title="Trash"
                        onClick={() => onNavigate("trash")}
                        data-testid="vault-trash-btn"
                    >
                        <Trash2 size={15} />
                        {trashCount > 0 && (
                            <span
                                style={{
                                    marginLeft: 4,
                                    fontSize: 10,
                                    color: C.muted,
                                }}
                            >
                                {trashCount}
                            </span>
                        )}
                    </IconBtn>
                    <IconBtn
                        title="Settings"
                        onClick={() => onNavigate("settings")}
                        data-testid="vault-settings-btn"
                    >
                        <SettingsIcon size={15} />
                    </IconBtn>
                    <IconBtn
                        title="Lock"
                        onClick={onLock}
                        data-testid="vault-lock-btn"
                    >
                        <LockIcon size={15} />
                    </IconBtn>
                </div>
            </div>

            {/* Body */}
            <div
                style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: "20px 20px 100px",
                }}
            >
                {/* Search */}
                <div
                    style={{
                        position: "relative",
                        marginBottom: 14,
                    }}
                >
                    <Search
                        size={14}
                        style={{
                            position: "absolute",
                            left: 14,
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: C.dim,
                        }}
                    />
                    <input
                        placeholder="Search vault…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{
                            width: "100%",
                            padding: "11px 14px 11px 38px",
                            background: "rgba(255,255,255,0.04)",
                            border: `1px solid ${C.border}`,
                            borderRadius: 10,
                            color: C.text,
                            fontSize: 13,
                            outline: "none",
                            boxSizing: "border-box",
                            fontFamily: C.font,
                        }}
                        data-testid="vault-search-input"
                    />
                </div>

                {/* Expiring banner */}
                {expiring.length > 0 && (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "10px 14px",
                            background: "rgba(217,119,6,0.08)",
                            border: `1px solid ${C.warn}33`,
                            borderRadius: 10,
                            marginBottom: 14,
                            color: C.warn,
                            fontSize: 12,
                        }}
                        data-testid="vault-expiring-banner"
                    >
                        <AlertTriangle size={14} />
                        {expiring.length} credential
                        {expiring.length > 1 ? "s" : ""} expiring within 30
                        days
                    </div>
                )}

                {/* Filter pills */}
                <div style={{ marginBottom: 16 }} data-testid="vault-filters">
                    <span
                        style={sPill(filter === "all")}
                        onClick={() => setFilter("all")}
                        data-testid="filter-all"
                    >
                        All · {entries.length}
                    </span>
                    {CRED_TYPES.map((t) => {
                        const count = entries.filter(
                            (e) => e.type === t.id,
                        ).length;
                        if (count === 0) return null;
                        return (
                            <span
                                key={t.id}
                                style={sPill(filter === t.id, t.color)}
                                onClick={() => setFilter(t.id)}
                                data-testid={`filter-${t.id}`}
                            >
                                {t.label} · {count}
                            </span>
                        );
                    })}
                </div>

                {/* Entry list */}
                {filtered.length === 0 ? (
                    <EmptyState
                        hasAny={entries.length > 0}
                        onNewEntry={onNewEntry}
                    />
                ) : (
                    filtered.map((e) => (
                        <EntryCard
                            key={e.id}
                            entry={e}
                            onClick={() => onOpenEntry(e)}
                        />
                    ))
                )}
            </div>

            {/* FAB — positioned to avoid the fixed "Made with Emergent" badge (bottom-right) */}
            <button
                onClick={onNewEntry}
                style={{
                    position: "fixed",
                    bottom: 72,
                    right: 24,
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${C.accent}, #065670)`,
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    boxShadow: "0 12px 32px rgba(11,126,163,0.4)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 50,
                }}
                title="New entry"
                data-testid="vault-fab-new"
            >
                <Plus size={22} />
            </button>
        </div>
    );
}

function IconBtn({ children, onClick, title, ...rest }) {
    return (
        <button
            onClick={onClick}
            title={title}
            style={{
                background: "transparent",
                border: `1px solid ${C.border}`,
                borderRadius: 10,
                color: C.muted,
                padding: "7px 10px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                fontFamily: C.font,
                fontSize: 12,
            }}
            {...rest}
        >
            {children}
        </button>
    );
}

function EntryCard({ entry, onClick }) {
    const type = CRED_TYPES.find((t) => t.id === entry.type) || CRED_TYPES[0];
    const Icon = ICONS[type.icon] || FileText;
    const primary = entry.fields[type.fields[0]] || "(untitled)";
    const secondary = entry.fields[type.fields[1]] || "";
    const exp = entry.fields?.Expiry;
    const expC = exp ? expColor(exp) : null;
    const expL = exp ? expLabel(exp) : null;
    return (
        <div
            style={sCard}
            onClick={onClick}
            onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = type.color + "55")
            }
            onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = C.border)
            }
            data-testid="vault-entry-card"
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                }}
            >
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        minWidth: 0,
                        flex: 1,
                    }}
                >
                    <div
                        style={{
                            width: 36,
                            height: 36,
                            borderRadius: 10,
                            background: type.color + "18",
                            border: `1px solid ${type.color}33`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: type.color,
                            flexShrink: 0,
                        }}
                    >
                        <Icon size={15} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                            style={{
                                fontSize: 14,
                                color: C.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {primary}
                        </div>
                        <div
                            style={{
                                fontSize: 11,
                                color: C.dim,
                                marginTop: 2,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {type.label}
                            {secondary && ` · ${secondary}`}
                        </div>
                    </div>
                </div>
                {exp && (
                    <div style={sBadge(expC)} data-testid="entry-expiry-badge">
                        {expL}
                    </div>
                )}
            </div>
        </div>
    );
}

function EmptyState({ hasAny, onNewEntry }) {
    return (
        <div
            style={{
                padding: "60px 20px",
                textAlign: "center",
                color: C.dim,
            }}
            data-testid="vault-empty"
        >
            <div
                style={{
                    fontSize: 48,
                    color: C.border,
                    marginBottom: 16,
                    fontWeight: 200,
                    letterSpacing: "0.2em",
                }}
            >
                ·  ·  ·
            </div>
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 4 }}>
                {hasAny ? "No matches" : "Your vault is empty"}
            </div>
            <div style={{ fontSize: 11 }}>
                {hasAny
                    ? "Try a different search or filter"
                    : "Add your first credential to get started"}
            </div>
            {!hasAny && (
                <button
                    style={{ ...sBtn("primary"), maxWidth: 240, margin: "24px auto 0" }}
                    onClick={onNewEntry}
                    data-testid="vault-empty-new-btn"
                >
                    <Plus size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                    New entry
                </button>
            )}
        </div>
    );
}
